import { getTeam, createTeamNew, deleteTeamNew, editTeamNew } from "frontend/api";
import { teamCacheAtom } from "state-atoms";
import { Team, TeamCache } from "shared/datamodel/schemas/team";
import { useGenericCache } from "./use-general-cache";
import { useTeamsWithProjects } from "./use-teams-with-projects";
import { useCallback } from "react";
import { useBoardsSections } from "./use-boards-sections";
import { getSectionId, BoardSectionType } from "shared/datamodel/schemas/board-section";

export function useTeamsCache() {
  const { addTeam, deleteTeam, updateTeam, getTeamsProjects } = useTeamsWithProjects();
  const { filterDeletedBoardsFromInitialSections } = useBoardsSections();
  const { getItems, isLoading, createItemCache, removeItemCache, updateItemCache } = useGenericCache<
    Team,
    TeamCache,
    "documentId",
    { name: string }
  >({
    cacheAtom: teamCacheAtom,
    fetchByIds: async (ids: string[]) => {
      const teams = await Promise.all(ids.map((id) => getTeam(id)));
      return teams;
    },
    entityName: "teams",
    entityKey: "team",
    keyField: "documentId",
    createItem: async (item: { name: string }) => {
      const team = await createTeamNew(item);
      addTeam(team);
      return team;
    },
    removeItem: async (item: Partial<Team>) => {
      if (item.documentId) {
        filterDeletedBoardsFromInitialSections(getSectionId(BoardSectionType.WORKSPACE, item.documentId));
        const projects = getTeamsProjects(item.documentId);
        if (projects) {
          for (const project of projects) {
            filterDeletedBoardsFromInitialSections(getSectionId(BoardSectionType.PROJECT, project.documentId));
          }
        }
        await deleteTeamNew(item.documentId);
        deleteTeam(item.documentId);
        return true;
      }
      return false;
    },
    updateItem: async (item: Partial<Team>) => {
      if (!item.documentId) throw new Error("Team document ID is required");
      const team = await editTeamNew({ ...item, teamId: item.documentId });
      updateTeam(item.documentId, item);
      if (!team) throw new Error("Failed to update team");
      return team;
    },
  });

  const leaveTeam = useCallback(
    async (item: Partial<Team>) => {
      if (item.documentId && (await updateItemCache(item))) {
        deleteTeam(item.documentId);
      }
    },
    [deleteTeam, updateItemCache]
  );

  return {
    getTeams: getItems,
    isLoading,
    createTeam: createItemCache,
    removeTeam: removeItemCache,
    updateTeam: updateItemCache,
    leaveTeam,
  };
}
